import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { animated, useSpring } from 'react-spring';
import useInterval from '@use-it/interval';
import { useSelector, useDispatch } from 'react-redux';

import LeaderboardTable from './BoardTable';
import Spinner from '../../../shared/_TBComponents/Spinner';
import SubText from '../../../shared/_TBComponents/SubText';
import { fetchLeaderboard } from '../ducks/leaderboardActions';

const LoadMoreContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 60px;
  padding: 20px 15px;
  justify-content: center;
  position: relative;
`;

// No position:relative here — tableRef.current.offsetParent must reach PageWrapper
// so that initialScrollLocation reflects the full viewport offset (~470px), keeping
// tableEnd in a range that rect.top (viewport-relative) can actually reach.
const tableAreaStyle = {
  flexGrow: 1,
};

const LeaderboardTableWrapper = ({
  activeBoards,
  id,
  windowSize,
  scrollAmount,
  setScrollAmount,
  scrollContainerRef,
  podiumsRef,
}) => {
  const tableRef = useRef();
  const [tableHeight, setTableHeight] = useState();
  const [tableContainerHeight, setTableContainerHeight] = useState(0);
  const [currentScroll, setCurrentScroll] = useState(0);

  const leaderboardData = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardData,
  );
  const leaderboardForm = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardForm,
  );
  const leaderboardSettings = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].settings,
  );
  const isEndOfList = useSelector((state) => state.leaderboard.data.leaderboards[id].isEndOfList);
  const isLeaderboardLoading = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].isLeaderboardLoading,
  );

  const [data, setData] = useState([]);

  useEffect(() => {
    setData(
      (activeBoards !== 1 || windowSize.width <= 768)
        ? leaderboardData.slice(1)
        : leaderboardData.slice(3),
    );
  }, [activeBoards, leaderboardData, windowSize]);

  const {
    autoScroll,
  } = leaderboardSettings;

  const dispatch = useDispatch();

  // React Spring animations for loading more indicator opacity.
  const loadTransition = useSpring({
    position: 'absolute',
    opacity: isLeaderboardLoading ? 1 : 0,
  });

  const endTransition = useSpring({
    position: 'absolute',
    opacity: isEndOfList ? 1 : 0,
  });

  // Auto Scroll: advance by half the visible table area height every 5 seconds,
  // clamping to the container's actual max scrollable distance so we always reach the bottom.
  useInterval(() => {
    const container = scrollContainerRef.current;
    const maxScroll = container ? container.scrollHeight - container.clientHeight : Infinity;
    setScrollAmount(Math.min(scrollAmount + (tableContainerHeight / 2), maxScroll));
  }, !autoScroll ? null : 5000);

  // Derive the visible table area height: total scroll container height minus podiums height.
  // Neither the scroll container nor tableAreaStyle has position:relative, so tableRef's
  // offsetParent resolves to PageWrapper and initialScrollLocation stays correct (~470px).
  useEffect(() => {
    const container = scrollContainerRef.current;
    const podiums = podiumsRef.current;
    if (!container || !podiums) return;
    setTableContainerHeight(container.offsetHeight - podiums.offsetHeight);
  }, [
    scrollContainerRef.current && scrollContainerRef.current.offsetHeight,
    podiumsRef.current && podiumsRef.current.offsetHeight,
  ]);

  // INFINITE SCROLL VARIABLES:

  // Margins in the page
  const tableMargins = 30;

  // Initial Scroll location using react refs.
  const initialScrollLocation = tableRef.current ? tableRef.current.offsetTop : 0;

  // tableEnd is the location of the end of the list by subtracting the initial scroll location
  // from the table list height, then adding the container height and subtracting both margins.
  const tableEnd = (initialScrollLocation - tableHeight)
    + tableContainerHeight - (tableMargins * 2) + 10;

  // Load More function which fetches the next leaderboard page if it's not the end of the list.
  const loadMore = () => {
    if (!isEndOfList) {
      dispatch(fetchLeaderboard(leaderboardForm, 'loadMore', id));
    }
  };

  // If the board is at the end of the list, reset the auto scroll position to the top.
  useEffect(() => {
    if (autoScroll === true && isEndOfList && (currentScroll <= tableEnd)) {
      setTimeout(() => {
        setScrollAmount(0);
        setCurrentScroll(0);
      }, 1500);
    }
  }, [scrollAmount]);

  return (
    <div style={tableAreaStyle}>
      <LeaderboardTable
        activeBoards={activeBoards}
        handleLoadMore={loadMore}
        leaderboardData={data}
        leaderboardForm={leaderboardForm}
        tableEnd={tableEnd}
        tableRef={tableRef}
        setTableHeight={setTableHeight}
        setCurrentScroll={setCurrentScroll}
        windowSize={windowSize}
      />
      <LoadMoreContainer>
        <animated.div style={loadTransition}>
          <Spinner
            saving
            darkTheme
            large={false}
          />
        </animated.div>
        <animated.div style={endTransition}>
          <SubText>
            End of Leaderboard
          </SubText>
        </animated.div>
      </LoadMoreContainer>
    </div>
  );
};

LeaderboardTableWrapper.propTypes = {
  activeBoards: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  windowSize: PropTypes.instanceOf(Object).isRequired,
  scrollAmount: PropTypes.number.isRequired,
  setScrollAmount: PropTypes.func.isRequired,
  scrollContainerRef: PropTypes.instanceOf(Object).isRequired,
  podiumsRef: PropTypes.instanceOf(Object).isRequired,
};

export default LeaderboardTableWrapper;
